/*============================================================================
  Cart Drawer Upsell — <cart-upsell> (Refactored & Robust)
==============================================================================*/

class CartUpsell extends HTMLElement {
  static MAX = 4;

  constructor() {
    super();
    this.cart = new Set();
    // Pre-bind to ensure removeEventListener works correctly
    this._onProductAdded = this._handleAjax.bind(this);
    this._onCartUpdated = this._handleCart.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector("[data-upsell-slider]");
    if (!this.slider) return;

    const staticEl = this.querySelector("[data-upsell-static-products]");
    try {
      this.static = staticEl ? JSON.parse(staticEl.textContent) : null;
    } catch (e) {
      console.error("[CartUpsell] Failed to parse static products:", e);
      this.static = null;
    }

    document.addEventListener("ajaxProduct:added", this._onProductAdded);
    document.addEventListener("cart:updated", this._onCartUpdated);
    this.refresh();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.ac?.abort();
    document.removeEventListener("ajaxProduct:added", this._onProductAdded);
    document.removeEventListener("cart:updated", this._onCartUpdated);
  }

  _handleAjax() {
    if (this.selfAdd) {
      this.selfAdd = false;
      return;
    }
    this._debounce();
  }

  _handleCart() {
    this._debounce();
  }

  _debounce() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.refresh(), 500);
  }

  async refresh() {
    try {
      const cart = await theme.cart.getCart();
      if (!this.slider) return;

      // FIX: Force IDs to Numbers to prevent String vs Number mismatches
      this.cart = new Set(cart.items.map((i) => Number(i.product_id)));

      if (this.static) return this._render(this.static);

      this.cart.size ? this._fetchRecs(cart.items[0].product_id) : this._hide();
    } catch (err) {
      console.error("[CartUpsell] Refresh Error:", err);
    }
  }

  async _fetchRecs(id) {
    this.ac?.abort();
    this.ac = new AbortController();
    try {
      const res = await fetch(
        `/recommendations/products.json?product_id=${id}&limit=8&intent=related`,
        { signal: this.ac.signal },
      );
      const { products } = await res.json();
      products?.length ? this._render(this._normalize(products)) : this._hide();
    } catch (e) {
      if (e.name !== "AbortError") this._hide();
    }
  }

  _normalize(prods) {
    const hasBundleTag = (item) => {
      const tags = Array.isArray(item.tags)
        ? item.tags
        : typeof item.tags === "string"
          ? item.tags.split(",")
          : [];
      return tags.some((t) => t.trim().toLowerCase() === "bundle");
    };

    return prods
      .filter((p) => !hasBundleTag(p))
      .map((p) => ({
        ...p,
        id: Number(p.id),
        url: p.url || `/products/${p.handle}`,
        compare_at_price: Number(p.compare_at_price) || 0,
        // Robust image formatting
        featured_image: p.featured_image
          ? p.featured_image.replace(/(\.(?:jpg|jpeg|png|webp))/, "_300x$1")
          : "",
        options_with_values:
          p.options?.map((o) => ({ name: o.name, values: o.values })) || [],
        has_only_default_variant:
          p.variants.length === 1 && p.variants[0].title.includes("Default"),
      }));
  }

  _render(products) {
    // FIX: Match against Number IDs
    const filtered = products
      .filter((p) => !this.cart.has(Number(p.id)))
      .slice(0, CartUpsell.MAX);
    if (!filtered.length) return this._hide();

    const key = filtered.map((p) => p.id).join();
    if (key === this.renderedKey) return;
    this.renderedKey = key;

    this.slider.innerHTML = "";
    filtered.forEach((p) => this.slider.appendChild(this._createCard(p)));
    this.classList.remove("hide");
  }

  _hide() {
    this.classList.add("hide");
    this.renderedKey = null;
  }

  _parseTitle(t) {
    const p = t.split(" - ");
    if (p.length < 2) return t;
    return p.slice(1).join(" - ").trim();
  }

  _formatPrice(a) {
    return theme.Currency.formatMoney(a, theme.settings.moneyFormat);
  }

  _createCard(p) {
    const card = document.createElement("div");
    card.className = "cart-upsell__card";

    const title = this._esc(this._parseTitle(p.title));
    const img = p.featured_image
      ? `<img src="${p.featured_image}" alt="${title}" class="cart-upsell__image" loading="lazy" width="64" height="64">`
      : `<div class="cart-upsell__image cart-upsell__image--placeholder"></div>`;

    const price =
      p.compare_at_price > p.price
        ? `<span class="cart-upsell__price cart-upsell__price--sale">${this._formatPrice(p.price)}</span><span class="cart-upsell__price cart-upsell__price--compare">${this._formatPrice(p.compare_at_price)}</span>`
        : `<span class="cart-upsell__price">${this._formatPrice(p.price)}</span>`;

    const btnLabel = !p.available
      ? theme.strings.soldOut
      : p.has_only_default_variant
        ? theme.strings.addToCart
        : theme.strings.choose;

    card.innerHTML = `
      <div class="cart-upsell__top">
        <a href="${p.url}" class="cart-upsell__image-link">${img}</a>
        <div class="cart-upsell__info">
          <a href="${p.url}" class="cart-upsell__product-title">${title}</a>
          <div class="cart-upsell__prices">${price}</div>
        </div>
      </div>
      <div class="cart-upsell__action">
        <button type="button" class="btn ${p.available ? "btn--primary" : "btn--secondary disabled"}" data-upsell-add ${!p.available ? "disabled" : ""}>${btnLabel}</button>
        <div class="cart-upsell__variants hide" data-upsell-variants></div>
      </div>`;

    card.querySelector("[data-upsell-add]").onclick = (e) => {
      e.preventDefault();
      p.has_only_default_variant
        ? this._add(p.variants[0].id, card)
        : this._showPicker(p, card);
    };

    return card;
  }

  _showPicker(p, card) {
    const cont = card.querySelector("[data-upsell-variants]");
    const chooseBtn = card.querySelector("[data-upsell-add]");

    chooseBtn.classList.add("hide");
    cont.classList.remove("hide");

    const keys = ["option1", "option2", "option3"].filter(
      (k) => p.variants[0]?.[k] != null,
    );
    const firstAvail = p.variants.find((v) => v.available) || p.variants[0];
    let html = "";

    keys.forEach((k, i) => {
      const vals = [...new Set(p.variants.map((v) => v[k]).filter(Boolean))];
      const name = this._esc(
        p.options_with_values?.[i]?.name || `Option ${i + 1}`,
      );
      const isHidden = vals.length < 2;

      html += `<select class="cart-upsell__select" aria-label="${name}" ${isHidden ? 'style="display:none"' : ""}>`;
      vals.forEach((val) => {
        const isSelected = val === firstAvail[k] ? "selected" : "";
        html += `<option value="${val}" ${isSelected}>${val}</option>`;
      });
      html += `</select>`;
    });

    html += `<button type="button" class="btn btn--primary">${theme.strings.addToCart}</button>`;
    cont.innerHTML = html;

    const selects = [...cont.querySelectorAll("select")];
    const addBtn = cont.querySelector("button");

    // Focus first select for accessibility
    selects[0]?.focus();

    const update = () => {
      // FIX: Improved matching to handle non-existent variant combinations
      const match = p.variants.find((v) =>
        keys.every((k, i) => v[k] === selects[i].value),
      );
      const isAvail = match?.available;

      addBtn.disabled = !match || !isAvail;
      addBtn.className = isAvail
        ? "btn btn--primary"
        : "btn btn--secondary disabled";

      if (!match) {
        addBtn.textContent = theme.strings.unavailable || "Unavailable";
      } else {
        addBtn.textContent = isAvail
          ? theme.strings.addToCart
          : theme.strings.soldOut;
      }

      addBtn.onclick = () => isAvail && this._add(match.id, card);
    };

    selects.forEach((s) => (s.onchange = update));
    update();
  }

  _esc(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  async _add(id, card) {
    card.classList.add("cart-upsell__card--loading");
    try {
      const res = await fetch(theme.routes.cartAdd, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });

      if (!res.ok) throw new Error("Add to cart failed");

      const productData = await res.json();
      this.selfAdd = true;

      document.dispatchEvent(
        new CustomEvent("ajaxProduct:added", {
          detail: { product: productData },
        }),
      );

      setTimeout(() => {
        card.classList.remove("cart-upsell__card--loading");
        this.refresh();
      }, 400);
    } catch (e) {
      console.error("[CartUpsell] Add error:", e);
      card.classList.remove("cart-upsell__card--loading");
    }
  }
}

if (!customElements.get("cart-upsell"))
  customElements.define("cart-upsell", CartUpsell);
